import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';
import { useSiteSettings } from '../../context/SiteSettingsContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { settings } = useSiteSettings();
  const footerSettings = settings.footer || {};
  const headerSettings = settings.header || {};
  const contact = settings.contact || {};
  const social = settings.social || {};

  const description = footerSettings.description || 'Trouvez un vétérinaire et prenez rendez-vous gratuitement en ligne.';
  const copyrightText = footerSettings.copyrightText || 'vetoRDV® - Tous droits réservés';
  const copyrightStartYear = footerSettings.copyrightStartYear || '2017';
  const slogan = footerSettings.slogan || 'Fait avec ❤️ pour les animaux';
  const showSocialLinks = footerSettings.showSocialLinks !== false;
  const showAdminLink = footerSettings.showAdminLink !== false;
  
  const logoTextPart1 = headerSettings.logoTextPart1 || 'VETO';
  const logoTextPart2 = headerSettings.logoTextPart2 || 'RDV';
  const logoType = headerSettings.logoType || 'default';
  const customLogoUrl = headerSettings.customLogoUrl || '';
  const logoColor = headerSettings.logoColor || '#22c55e';
  const logoColorDark = headerSettings.logoColorDark || '#16a34a';

  const renderLogo = () => {
    if (logoType === 'none') {
      return null;
    }
    
    if (logoType === 'custom' && customLogoUrl) {
      return (
        <img 
          src={customLogoUrl} 
          alt="Logo" 
          className="w-8 h-8 object-contain"
        />
      );
    }
    
    return (
      <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none">
        <path d="M20 8c-2 0-3.5 1.5-3.5 3.5S18 15 20 15s3.5-1.5 3.5-3.5S22 8 20 8z" fill={logoColor}/>
        <path d="M12 12c-1.5 0-2.5 1-2.5 2.5s1 2.5 2.5 2.5 2.5-1 2.5-2.5-1-2.5-2.5-2.5z" fill={logoColor}/>
        <path d="M28 12c-1.5 0-2.5 1-2.5 2.5s1 2.5 2.5 2.5 2.5-1 2.5-2.5-1-2.5-2.5-2.5z" fill={logoColor}/>
        <path d="M20 18c-6 0-10 4-10 9 0 3 2 5 5 5 2 0 3.5-1 5-3 1.5 2 3 3 5 3 3 0 5-2 5-5 0-5-4-9-10-9z" fill={logoColorDark}/>
      </svg>
    );
  };

  const defaultSections = [
    {
      id: '1',
      title: 'Navigation',
      links: [
        { id: '1', label: 'Trouver un vétérinaire', path: '/recherche' },
        { id: '2', label: 'Conseils', path: '/conseils' },
        { id: '3', label: 'Espace patient', path: '/espace-patient' },
        { id: '4', label: 'Espace vétérinaire', path: '/espace-veterinaire' },
      ]
    },
    {
      id: '2',
      title: 'Support',
      links: [
        { id: '1', label: 'Contact & FAQ', path: '/contact' },
      ]
    },
    {
      id: '3',
      title: 'Légal',
      links: [
        { id: '1', label: 'Mentions légales', path: '/mentions-legales' },
        { id: '2', label: 'CGU', path: '/cgu' },
        { id: '3', label: 'Confidentialité', path: '/confidentialite' },
        { id: '4', label: 'Vos droits RGPD', path: '/rgpd' },
      ]
    }
  ];

  const sections = footerSettings.sections || defaultSections;

  return (
    <footer className="bg-gray-50 dark:bg-gray-800 border-t dark:border-gray-700 mt-auto transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="col-span-1 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              {renderLogo()}
              <span className="text-lg font-bold">
                <span className="text-gray-800 dark:text-white">{logoTextPart1}</span>
                <span style={{ color: logoColor }}>{logoTextPart2}</span>
              </span>
            </Link>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
              {description}
            </p>
            
            {/* Social Links */}
            {showSocialLinks && (
              <div className="flex items-center gap-3">
                {social.facebook && (
                  <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 transition-colors">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {social.instagram && (
                  <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 transition-colors">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {social.twitter && (
                  <a href={social.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 transition-colors">
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {social.linkedin && (
                  <a href={social.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 transition-colors">
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Dynamic Sections */}
          {sections.map((section) => (
            <div key={section.id}>
              <h4 className="font-semibold text-gray-800 dark:text-white mb-4">{section.title}</h4>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.id}>
                    {link.isExternal ? (
                      <a 
                        href={link.path} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 text-sm transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : link.isEmail ? (
                      <a 
                        href={`mailto:${link.path}`}
                        className="text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 text-sm transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : link.isPhone ? (
                      <a 
                        href={`tel:${link.path.replace(/\s/g, '')}`}
                        className="text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 text-sm transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link 
                        to={link.path} 
                        className="text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 text-sm transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
                {/* Add contact info to Support section */}
                {section.title === 'Support' && (
                  <>
                    {contact.email && (
                      <li>
                        <a href={`mailto:${contact.email}`} className="text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 text-sm transition-colors">
                          {contact.email}
                        </a>
                      </li>
                    )}
                    {contact.phone && (
                      <li>
                        <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 text-sm transition-colors">
                          {contact.phone}
                        </a>
                      </li>
                    )}
                  </>
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* Copyright */}
        <div className="border-t dark:border-gray-700 mt-10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            © {copyrightStartYear}-{currentYear} {copyrightText}
          </p>
          <div className="flex items-center gap-6">
            <span className="text-xs text-gray-400 dark:text-gray-500">{slogan}</span>
            {showAdminLink && (
              <Link 
                to="/admin" 
                className="text-gray-300 hover:text-gray-500 text-xs transition-colors"
                title="Administration"
              >
                Administration
              </Link>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
